import { StateCreator } from "zustand";

import { SysMessage, UserInfo } from "models/user/user";
import { RootState } from "store";
import { jwtDecode } from "jwt-decode";
import { TokenDecoded } from "models/login/login";
import _ from "lodash";
import { UsersAPI } from "api/users";
import { failedLoginTeams, isInTeams, successLoginTeams } from "common/teams";
import { clearTabToken, setTabToken } from "common/MetaActions";
import Bugsnag from "@bugsnag/js";

type FetchUserLoginProps = {
  code: string;
  password: string;
  username: string;
};

export interface UserSlice {
  isNewPass: boolean;
  isAuthenticating: boolean;
  isAuthenticated: boolean;
  token?: string;
  userInfo?: UserInfo;
  loginFailed: boolean;
  loginTrue: boolean;
  showNotActivatedMessage: boolean;
  sysMessage: SysMessage;
  loginTimeout?: any;
  sessionExpired: boolean;
  setSessionExpired: (val: boolean) => void;

  loggingIn: () => void;
  loginUser: (token: string | null) => void;
  loginError: () => void;
  loginErrorNotActivated: () => void;
  fetchInternalUserLogin: (code: string | null) => void;
  fetchUserLogin: ({ code, password, username }: FetchUserLoginProps) => void;
  validateToken: (token: string) => void;
  setPasswordAndGetAuthFromKey: (key: string, password: string) => void;
}

export const createUserSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  UserSlice
> = (set, get) => ({
  isAuthenticating: false,
  isAuthenticated: false,
  token: undefined,
  userInfo: undefined,
  loginFailed: false,
  loginTrue: false,
  showNotActivatedMessage: false,
  sessionExpired: false,
  sysMessage: {
    message: "",
    validUntil: undefined
  },
  isNewPass: false,

  loggingIn: () =>
    set(
      ({ user }) => ({
        user: {
          ...user,
          loginTimeout: false,
          loginFailed: false,
          isAuthenticating: true
        }
      }),
      false,
      "user/LOGGING_IN"
    ),

  loginUser: (token) => {
    if (token === null) {
      return;
    }

    const tokenDecoded = jwtDecode<TokenDecoded>(token);
    set(
      ({ user }) => ({
        user: {
          ...user,
          loginFailed: false,
          loginTimeout: false,
          isAuthenticated: !!token,
          isAuthenticating: false,
          sessionExpired: false,
          token,
          userInfo: {
            ...tokenDecoded,
            type: _.startsWith(tokenDecoded.id, "External")
              ? "External"
              : "Internal"
          }
        }
      }),
      false,
      "user/LOGIN_USER"
    );
  },

  loginError: () =>
    set(
      ({ user }) => ({
        user: {
          ...user,
          loginFailed: true,
          isAuthenticated: false,
          isAuthenticating: false,
          userInfo: undefined,
          token: ""
        }
      }),
      false,
      "user/LOGIN_ERROR"
    ),

  loginErrorNotActivated: () =>
    set(
      ({ user }) => ({
        user: {
          ...user,
          loginFailed: true,
          showNotActivatedMessage: true,
          isAuthenticated: false,
          isAuthenticating: false,
          userInfo: undefined,
          token: ""
        }
      }),
      false,
      "user/LOGIN_ERROR_NOT_ACTIVATED"
    ),

  setSessionExpired: (val: boolean) =>
    set(
      ({ user }) => ({
        user: {
          ...user,
          sessionExpired: val
        }
      }),
      false,
      "user/SESSION_EXPIRED"
    ),

  fetchInternalUserLogin: async (code) => {
    set({}, false, "user/FETCH_INTERNAL_USER_LOGIN");
    get().user.loggingIn();
    try {
      const response = await UsersAPI.fetchInternalUserLogin(code);
      let loginUrl, token;

      try {
        loginUrl = response.loginUrl;
        token = response.token;
      } catch (error) {
        token = response;
      }

      if (loginUrl) {
        if (process.env.isLocal) {
          const redirectUri =
            window.location.origin +
            "/" +
            loginUrl
              .match(/(redirect_uri=)[^&]+/)[0]
              .split("=")[1]
              .split("/")
              .slice(3)
              .join("/");
          loginUrl = loginUrl.replace(
            /(redirect_uri=)[^&]+/,
            "$1" + redirectUri
          );
        }
        window.location.href = loginUrl;
        return;
      }

      if (isInTeams()) {
        return successLoginTeams(token);
      }
      setTabToken(token);
      get().user.loginUser(token);
    } catch (error) {
      get().error.appendErrorNotification(
        "Hämtning av intern användarinloggning misslyckades.",
        error
      );
      if (isInTeams()) {
        failedLoginTeams("Hämtning av intern användarinloggning misslyckades.");
      }
      let statusCode: number | undefined;

      if (error && typeof error === "object" && "status" in error) {
        statusCode = (error as { status: number }).status;
      }
      if (statusCode === 403) {
        get().user.loginErrorNotActivated();
      } else {
        get().user.loginError();
      }
      Bugsnag.notify(error as any);
    }
  },

  fetchUserLogin: async ({ code, password, username }) => {
    set({}, false, "user/FETCH_USER_LOGIN");
    get().user.loggingIn();
    try {
      const response = await UsersAPI.fetchUserLogin({
        code,
        password,
        username
      });
      let loginUrl, token;

      try {
        loginUrl = response.loginUrl;
        token = response.token;
        if (!token) {
          throw new Error("INVALID_TOKEN");
        }
      } catch (error) {
        const err = error as any;
        if (err.message === "INVALID_TOKEN") {
          clearTabToken();
          get().error.appendErrorNotification(
            "Hämtning av användarinloggning misslyckades.",
            error
          );

          setTimeout(() => {
            window.location.href = "/";
          }, 1500);

          return;
        }
        token = response.text;
      }

      if (loginUrl) {
        if (process.env.isLocal) {
          const redirectUri =
            window.location.origin +
            "/" +
            loginUrl
              .match(/(redirect_uri=)[^&]+/)[0]
              .split("=")[1]
              .split("/")
              .slice(3)
              .join("/");
          loginUrl = loginUrl.replace(
            /(redirect_uri=)[^&]+/,
            "$1" + redirectUri
          );
        }
        window.location.href = loginUrl;
        return;
      }

      if (isInTeams()) {
        return successLoginTeams(token);
      }

      setTabToken(token);
      get().user.loginUser(token);
    } catch (error) {
      get().error.appendErrorNotification(
        "Hämtning av användarinloggning misslyckades.",
        error
      );

      if (isInTeams()) {
        failedLoginTeams("Hämtning av användarinloggning misslyckades.");
      }

      let statusCode: number | undefined;

      if (error && typeof error === "object" && "status" in error) {
        statusCode = (error as { status: number }).status;
      }
      if (statusCode === 403) {
        get().user.loginErrorNotActivated();
      } else {
        get().user.loginError();
      }
      Bugsnag.notify(error as any);
    }
  },

  validateToken: async (token) => {
    set({}, false, "user/VALIDATE_TOKEN");
    try {
      const response = await UsersAPI.validateToken(token);
      setTabToken(response);
      get().user.loginUser(response);
    } catch (error) {
      get().error.appendErrorNotification(
        "Validering av token misslyckades.",
        error
      );
    }
  },

  setPasswordAndGetAuthFromKey: async (key, password) => {
    set({}, false, "user/SET_PASSWORD_AND_GET_AUTH_FROM_KEY");
    try {
      const response = await UsersAPI.setPasswordAndGetAuthFromKey(
        key,
        password
      );
      const tokenWithoutQuotes = response.replace(/^"|"$/g, "");
      setTabToken(tokenWithoutQuotes);
      get().user.loginUser(tokenWithoutQuotes);
    } catch (error) {
      get().error.appendErrorNotification(
        "Autentisering via nyckel misslyckades.",
        error
      );
    }
  }
});
